import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { richText } from '../../util/richText';
import useCardDecodedSizes from '../../hooks/useCardDecodedSizes';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  LayoutSingleColumn,
  NamedRedirect,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

/**
 * Editorial Masthead — the full-bleed maroon band directly under the header.
 * Holds the seller's avatar, name, credential pills, bio, and (owner-only) the
 * Edit-profile link. Each pill renders only when its underlying data exists.
 */
export const Masthead = props => {
  const { user, displayName, bio, publicData, showLinkToProfileSettingsPage, listingCount } = props;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  // Location pill — only when the seller actually has a location value.
  // (Users have no seller-location field today, so this stays omitted in practice.)
  const locationValue =
    typeof publicData?.location === 'string'
      ? publicData.location
      : publicData?.location?.address || publicData?.location?.city || null;

  // Member-since pill — explicit publicData.memberSince wins, else the account
  // creation year, else the pill is omitted.
  const createdYear = user?.attributes?.createdAt
    ? new Date(user.attributes.createdAt).getFullYear()
    : null;
  const memberSinceYear = publicData?.memberSince || createdYear || null;

  return (
    <div className={css.masthead}>
      {showLinkToProfileSettingsPage ? (
        <NamedLink className={css.editLink} name="ProfileSettingsPage">
          <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
        </NamedLink>
      ) : null}

      <div className={css.mastRow}>
        <AvatarLarge className={css.avatar} user={user} disableProfileLink />
        <div className={css.mastText}>
          {displayName ? <h1 className={css.sellerName}>{displayName}</h1> : null}

          <div className={css.pills}>
            {locationValue ? (
              <span className={css.pill}>
                <span className={css.pillIcon} aria-hidden="true">
                  ⌖
                </span>
                {locationValue}
              </span>
            ) : null}
            {memberSinceYear ? (
              <span className={css.pill}>
                <span className={css.pillIcon} aria-hidden="true">
                  ✦
                </span>
                <FormattedMessage
                  id="ProfilePage.memberSincePill"
                  values={{ year: memberSinceYear }}
                />
              </span>
            ) : null}
            <span className={css.pill}>
              <FormattedMessage
                id="ProfilePage.listingCountPill"
                values={{ count: listingCount }}
              />
            </span>
          </div>
        </div>
      </div>

      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}
    </div>
  );
};

/**
 * MainContent — the cream content section below the masthead: an editorial
 * "Horses for sale" opener (eyebrow + gold rule) followed by the listings grid.
 */
export const MainContent = props => {
  const { userShowError, listings, queryListingsError } = props;
  const hasListings = listings.length > 0;

  // 🔴 Story 12.15 (AC-5c) — ONE decoded-size map for the whole surface. Each
  // card reports its own `<img>`'s decode into it on load; nothing is fetched.
  const { decodedSizeFor, onImageDecoded } = useCardDecodedSizes();


  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  if (!hasListings) {
    return null;
  }

  return (
    <div className={css.content}>
      <div className={css.eyebrow}>
        <FormattedMessage id="ProfilePage.horsesForSaleEyebrow" />
      </div>
      <div className={css.goldRule} />
      <ul className={css.listings}>
        {listings.map(l => (
          <li className={css.listing} key={l.id.uuid}>
            <ListingCard
              listing={l}
              showAuthorInfo={false}
              decodedSize={decodedSizeFor(l)}
              onImageDecoded={onImageDecoded}
              sourcePage="seller_profile"
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    listings,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData } = profileUser?.attributes?.profile || {};
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={
          <div className={css.solidHeaderWrap}>
            <TopbarContainer variant="homepage" solid />
          </div>
        }
        footer={<FooterContainer />}
      >
        <Masthead
          user={profileUser}
          displayName={displayName}
          bio={bio}
          publicData={publicData}
          showLinkToProfileSettingsPage={mounted && isCurrentUser}
          listingCount={listings.length}
        />
        <MainContent
          userShowError={userShowError}
          listings={listings}
          queryListingsError={rest.queryListingsError}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
